<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Officer Profile Card -->
      <div class="bg-primary-50 rounded-xl shadow-lg p-6 mb-8 text-white">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div>
              <p class="text-sm opacity-90">Welcome back,</p>
              <p class="text-2xl font-bold">{{ currentUser.name }}</p>
              <p class="text-sm opacity-90 mt-1">
                Verification Officer • {{ myTickets.length }} active tickets
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <div class="text-center px-4 py-2 bg-white/20 rounded-lg">
              <p class="text-2xl font-bold">{{ completedToday }}</p>
              <p class="text-xs opacity-90">Completed Today</p>
            </div>
            <div class="text-center px-4 py-2 bg-white/20 rounded-lg">
              <p class="text-2xl font-bold">{{ successRate }}%</p>
              <p class="text-xs opacity-90">Success Rate</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">All Tickets</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ myTickets.length }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Pending</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ pendingCount }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">In Progress</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ inProgressCount }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">Completed</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ completedCount }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div
        class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6"
      >
        <div class="flex flex-col lg:flex-row gap-4">
          <div class="flex-1 relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
         <input
              v-model="filters.search"
              type="text"
              placeholder="Search by ticket #, client name, or phone..."
              class="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

           <div class="w-full lg:w-48">
           <input type="date" v-model="filters.date" @change="getTickets()" class="block w-full bg-white pl-3 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"> 
          </div>

          <div class="w-full lg:w-48">
            <select
              v-model="filters.status"
              class="block w-full bg-white pl-3 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Verified">Verified</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div class="w-full lg:w-48">
            <select
              v-model="filters.priority"
              class="block w-full bg-white pl-3 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <button
            @click="resetFilters"
            class="px-4 py-2 text-sm font-medium text-white hover:text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors"
          >
            Reset Filters
          </button>
        </div>
      </div>

      <!-- Tickets Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div
          v-for="ticket in filteredTickets"
          :key="ticket.id"
          class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
        >
          <!-- Ticket Header -->
          <div class="p-6 border-b border-gray-100">
            <div class="flex items-center justify-between mb-0">
              <span></span>
              <StatusBadge :status="ticket.decision" />
            </div>

            <div class="flex items-center text-sm text-gray-500 mb-2">
              <svg
                class="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {{ ticket.client?.full_name }}
            </div>

            <div class="flex items-center text-sm text-gray-500">
              <svg
                class="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
               {{ formatKenyanPhone(ticket.client?.phone) }}
            </div>
          </div>

          <!-- Call Attempts Summary -->
          <div class="px-6 py-3 bg-gray-50 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-gray-700"
                >Call Attempts: {{ ticket.callAttempts?.length || 0 }}</span
              >
              <span class="text-xs text-gray-500"
                >Last: {{ getLastCallAttempt(ticket) }}</span
              >
            </div>
          </div>

          <!-- Emergency Contacts -->
          <div class="px-6 py-3 border-b border-gray-100">
            <p
              class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2"
            >
              Emergency Contacts
            </p>
            <div class="space-y-2">
              <div
                v-for="contact in ticket.emergency_contacts"
                :key="contact.id"
                class="flex items-center justify-between text-sm"
              >
                <div class="flex items-center">
                  <span
                    class="w-2 h-2 rounded-full mr-2"
                    :class="{
                      'bg-green-500': contact.status === 'reached',
                      'bg-yellow-500': contact.status === 'pending',
                      'bg-red-500': contact.status === 'failed',
                    }"
                  ></span>
                  <span class="text-gray-600">{{ contact.name }}</span>
                  <span class="text-xs text-gray-400 ml-2"
                    >({{ contact.relationship }})</span
                  >
                </div>
                <span class="text-xs text-gray-500">{{ contact.phone }}</span>
              </div>
            </div>
          </div>

          <!-- Verification Progress -->
          <div class="px-6 py-3 border-b border-gray-100">
            <p
              class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2"
            >
              Verification Progress
            </p>
            <div class="flex items-center gap-2 flex-wrap">
              <span
                v-for="stage in verificationStages"
                :key="stage.key"
                class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                :class="getVerificationBadgeClass(ticket, stage.key)"
              >
                {{ stage.label }}: {{ getVerificationLabel(ticket, stage.key) }}
              </span>
            </div>
          </div>

          <!-- Actions -->
          <div class="px-6 py-4 flex items-center justify-between">
            <div class="flex items-center space-x-2"></div>

            <div class="flex items-center space-x-2">

              <button
                @click="viewTicketSMS(ticket.id)"
                class="inline-flex items-center px-3 py-1.5 bg-indigo-800 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <svg
                  class="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View SMS
              </button>
              <button
                @click="viewTicketDetails(ticket)"
                class="inline-flex items-center px-3 py-1.5 bg-indigo-800 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <svg
                  class="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View Details
              </button>
              <button
                v-if="ticket.status === 'In Progress'"
                @click="openCompleteModal(ticket)"
                class="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg
                  class="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Complete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="filteredTickets.length === 0"
        class="bg-white rounded-xl shadow-sm p-12 text-center"
      >
        <svg
          class="w-16 h-16 text-gray-300 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
        <p class="text-gray-500">
          No tickets found matching your filters.
        </p>
      </div>

      <!-- Pagination -->
      <div
        v-if="filteredTickets.length > 0"
        class="mt-8 flex items-center justify-between"
      >
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-500">Items per page:</span>
          <select
            v-model="itemsPerPage"
            class="border border-gray-200 rounded-lg text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="6">6</option>
            <option value="12">12</option>
            <option value="24">24</option>
          </select>
        </div>

        <div class="flex items-center space-x-4">
          <span class="text-sm text-gray-500">
            {{ paginationStart }}-{{ paginationEnd }} of
            {{ filteredTickets.length }}
          </span>
          <div class="flex items-center space-x-2">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Call Attempt Modal -->
      <div v-if="showCallModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div
          class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="closeCallModal"
          ></div>

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-gray-900">
                  Log Call Attempt - {{ selectedTicket?.ticketNumber }}
                </h3>
                <button
                  @click="closeCallModal"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form @submit.prevent="submitCallAttempt">
                <!-- Contact Selection -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Contact Type</label
                  >
                  <div class="flex space-x-4">
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.contactType"
                        value="customer"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700">Customer</span>
                    </label>
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.contactType"
                        value="emergency"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700"
                        >Emergency Contact</span
                      >
                    </label>
                  </div>
                </div>

                <!-- Emergency Contact Selection -->
                <div v-if="callForm.contactType === 'emergency'" class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Select Contact</label
                  >
                  <select
                    v-model="callForm.contactId"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a contact</option>
                    <option
                      v-for="contact in selectedTicket?.emergencyContacts"
                      :key="contact.id"
                      :value="contact.id"
                    >
                      {{ contact.name }} ({{ contact.relationship }}) -
                      {{ contact.phone }}
                    </option>
                  </select>
                </div>

                <!-- Phone Number -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Phone Number</label
                  >
                  <input
                    v-model="callForm.phoneNumber"
                    type="tel"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter phone number"
                  />
                </div>

                <!-- Call Outcome -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Outcome</label
                  >
                  <select
                    v-model="callForm.outcome"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    @change="handleOutcomeChange"
                  >
                    <option value="">Select outcome</option>
                    <option value="reached">Reached - Successful</option>
                    <option value="confirmed">
                      Confirmed - Information Verified
                    </option>
                    <option value="no_answer">No Answer</option>
                    <option value="busy">Busy</option>
                    <option value="wrong_number">Wrong Number</option>
                    <option value="not_reachable">Not Reachable</option>
                    <option value="call_back_later">Call Back Later</option>
                    <option value="denied_relationship">
                      Denied Relationship
                    </option>
                    <option value="language_barrier">Language Barrier</option>
                    <option value="deceased">Contact Deceased</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <!-- Notes -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Notes</label
                  >
                  <textarea
                    v-model="callForm.notes"
                    rows="3"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add any notes about the call..."
                  ></textarea>
                </div>

                <!-- Next Steps (conditional) -->
                <div
                  v-if="showNextSteps"
                  class="mb-4 p-4 bg-blue-50 rounded-lg"
                >
                  <h4 class="text-sm font-medium text-blue-800 mb-2">
                    Next Steps
                  </h4>
                  <div class="space-y-2">
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.nextStep"
                        value="callback"
                        class="mr-2"
                      />
                      <span class="text-sm text-blue-700"
                        >Schedule a callback</span
                      >
                    </label>
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.nextStep"
                        value="escalate"
                        class="mr-2"
                      />
                      <span class="text-sm text-blue-700"
                        >Escalate to supervisor</span
                      >
                    </label>
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="callForm.nextStep"
                        value="complete"
                        class="mr-2"
                      />
                      <span class="text-sm text-blue-700"
                        >Mark as ready for completion</span
                      >
                    </label>
                  </div>

                  <!-- Callback Time -->
                  <div v-if="callForm.nextStep === 'callback'" class="mt-3">
                    <label class="block text-sm font-medium text-blue-700 mb-1"
                      >Callback Time</label
                    >
                    <input
                      v-model="callForm.callbackTime"
                      type="datetime-local"
                      class="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <!-- Submit Buttons -->
                <div class="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    @click="closeCallModal"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    :disabled="submitting"
                  >
                    {{ submitting ? "Logging..." : "Log Call Attempt" }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Call History Modal -->
      <div v-if="showHistoryModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div
          class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="closeHistoryModal"
          ></div>

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-gray-900">
                  Call History - {{ selectedTicket?.ticket_number }}
                </h3>
                <button
                  @click="closeHistoryModal"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Call History Timeline -->
              <div class="space-y-4 max-h-96 overflow-y-auto pr-2">
                <div
                  v-for="attempt in selectedTicket?.callAttempts"
                  :key="attempt.id"
                  class="relative pl-8 pb-4 border-l-2 border-gray-200 last:border-0"
                >
                  <!-- Timeline dot -->
                  <div
                    class="absolute left-[-9px] w-4 h-4 rounded-full border-2 border-white"
                    :class="outcomeColors[attempt.outcome]"
                  ></div>

                  <!-- Attempt content -->
                  <div class="bg-gray-50 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center space-x-2">
                        <span class="text-sm font-medium text-gray-900"
                          >Attempt #{{ attempt.attemptNumber }}</span
                        >
                        <OutcomeBadge :outcome="attempt.outcome" />
                      </div>
                      <span class="text-xs text-gray-500">{{
                        formatDateTime(attempt.timestamp)
                      }}</span>
                    </div>

                    <div class="grid grid-cols-2 gap-2 text-sm mb-2">
                      <div>
                        <span class="text-gray-500">Contact:</span>
                        <span class="ml-2 text-gray-900">{{
                          attempt.contactName
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500">Phone:</span>
                        <span class="ml-2 text-gray-900">{{
                          attempt.phoneNumber
                        }}</span>
                      </div>
                    </div>

                    <p v-if="attempt.notes" class="text-sm text-gray-600 mt-2">
                      {{ attempt.notes }}
                    </p>

                    <!-- Next step if any -->
                    <div
                      v-if="attempt.nextStep"
                      class="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block"
                    >
                      Next: {{ attempt.nextStep }}
                      <span v-if="attempt.callbackTime" class="ml-1"
                        >at {{ formatTime(attempt.callbackTime) }}</span
                      >
                    </div>
                  </div>
                </div>

                <div
                  v-if="!selectedTicket?.callAttempts?.length"
                  class="text-center py-8 text-gray-500"
                >
                  No call attempts yet
                </div>
              </div>

              <div class="mt-6 flex justify-end">
                <button
                  @click="closeHistoryModal"
                  class="px-4 py-2 border border-indigo-800 text-indigo-800 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Complete Ticket Modal -->
      <div v-if="showCompleteModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div
          class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="closeCompleteModal"
          ></div>

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">
                Complete Verification - {{ selectedTicket?.ticketNumber }}
              </h3>

              <form @submit.prevent="submitCompletion">
                <!-- Final Decision -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Final Decision</label
                  >
                  <select
                    v-model="completionForm.decision"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select decision</option>
                    <option value="verified">
                      Verified - All information confirmed
                    </option>
                    <option value="rejected">
                      Rejected - Unable to verify
                    </option>
                    <option value="pending_review">
                      Pending Review - Needs supervisor attention
                    </option>
                  </select>
                </div>

                <!-- Summary -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Verification Summary</label
                  >
                  <textarea
                    v-model="completionForm.summary"
                    required
                    rows="4"
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Provide a detailed summary of the verification process..."
                  ></textarea>
                </div>

                <!-- Call Attempts Summary -->
                <div class="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h4 class="text-sm font-medium text-gray-700 mb-2">
                    Call Attempts Summary
                  </h4>
                  <p class="text-sm text-gray-600">
                    Total attempts:
                    {{ selectedTicket?.callAttempts?.length || 0 }}
                  </p>
                  <p class="text-sm text-gray-600">
                    Successful contacts: {{ successfulCallCount }}
                  </p>
                </div>

                <!-- Submit Buttons -->
                <div class="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    @click="closeCompleteModal"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    :disabled="submitting"
                  >
                    {{ submitting ? "Submitting..." : "Complete Verification" }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Success Notification -->
      <div
        v-if="showNotification"
        class="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 animate-slide-up z-50"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{{ notificationMessage }}</span>
      </div>

      <!-- Floating Action Button -->
      <button
        @click="getTickets"
        class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-800 text-white shadow-lg transition-all duration-300 hover:bg-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label="Refresh page"
      >
        <!-- Refresh icon (SVG) -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          :class="{ 'animate-spin': isRefreshing }"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      </button>
    </div>

    <div
      v-if="isRefreshing"
      class="fixed inset-0 ml-64 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div class="relative">
        <!-- Outer ring -->
        <div class="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
        <!-- Inner spinning gradient ring -->
        <div
          class="w-16 h-16 border-4 border-transparent rounded-full absolute top-0 left-0 border-t-indigo-500 border-r-indigo-700 animate-spin"
        ></div>
        <!-- Center dot -->
        <div
          class="w-3 h-3 bg-indigo-800 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import OutcomeBadge from "@/components/ui/OutcomeBadge.vue";
import { useAuthStore } from "@/stores/authStore";
import api from "@/utils/api";
import { useNotifications } from "@/composables/useNotifications";

// Notification system
const { showError, formatValidationErrors } = useNotifications();

const authStore = useAuthStore();

const router = useRouter();

// Current user
const currentUser = ref({});

if (authStore.isAuthenticated) {
  currentUser.value = authStore.user;
}

// State
const isRefreshing = ref(false);

// Outcome colors for timeline
const outcomeColors = {
  reached: "bg-green-500",
  confirmed: "bg-green-500",
  no_answer: "bg-yellow-500",
  busy: "bg-yellow-500",
  wrong_number: "bg-red-500",
  not_reachable: "bg-red-500",
  call_back_later: "bg-blue-500",
  denied_relationship: "bg-orange-500",
  language_barrier: "bg-purple-500",
  deceased: "bg-gray-500",
  other: "bg-gray-400",
};

// Verification stages shown on each ticket card
const verificationStages = [
  { key: "kyc_verifications", label: "KYC" },
  { key: "contact_verifications", label: "Contact" },
  { key: "sms_verifications", label: "SMS" },
  { key: "final_verifications", label: "Final" },
];

// Tickets
const tickets = ref([]);

// Filters
const filters = ref({
  search: "",
  status: "",
  priority: "",
  date: "",
});

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(6);

// Modal state
const showCallModal = ref(false);
const showHistoryModal = ref(false);
const showCompleteModal = ref(false);
const selectedTicket = ref(null);
const submitting = ref(false);
const showNextSteps = ref(false);

// Form state
const callForm = ref({
  contactType: "customer",
  contactId: "",
  phoneNumber: "",
  outcome: "",
  notes: "",
  nextStep: "",
  callbackTime: "",
});

const completionForm = ref({
  decision: "",
  summary: "",
});

// Notification state
const showNotification = ref(false);
const notificationMessage = ref("");

// Computed properties
const myTickets = computed(() => {
  return tickets.value;
});

const pendingCount = computed(() => {
  return myTickets.value.filter((t) => t.decision === "Pending").length;
});

const inProgressCount = computed(() => {
  return myTickets.value.filter((t) => t.decision === "In Progress").length;
});

const completedCount = computed(() => {
  return myTickets.value.filter(
    (t) => t.decision === "Verified" || t.decision === "Rejected"
  ).length;
});

const completedToday = computed(() => {
  const today = new Date().toDateString();
  return myTickets.value.filter(
    (t) =>
      t.decision === "Verified" ||
      (t.decision === "Rejected" &&
        new Date(t.updatedAt || t.createdAt).toDateString() === today)
  ).length;
});

const successRate = computed(() => {
  const total = myTickets.value.length;
  const completed = completedCount.value;
  return total > 0 ? Math.round((completed / total) * 100) : 0;
});

const formatKenyanPhone = (phoneNumber) => {
  if (!phoneNumber) return "N/A";

  try {
    // Convert to string and remove any non-digit characters
    const cleaned = String(phoneNumber).replace(/\D/g, "");

    // Check if it's a Kenyan number starting with 254
    if (cleaned.startsWith("254")) {
      // Remove '254' and ensure we have the remaining 9 digits
      const localNumber = cleaned.substring(3);

      // If we have exactly 9 digits after removing 254, format with leading 0
      if (localNumber.length === 9) {
        return "0" + localNumber;
      }
      // If we have 12 digits total (254 + 9 digits), take the last 9
      else if (cleaned.length === 12) {
        return "0" + cleaned.slice(-9);
      }
    }

    // Return original cleaned number if not in Kenyan format
    return cleaned;
  } catch (error) {
    console.log(error);
    return "N/A";
  }
};

// Backend returns each verification relation ordered latest()->get(),
// so index 0 is always the most recent record for that stage
const getLatestVerification = (ticket, key) => {
  const records = ticket[key];
  return records && records.length ? records[0] : null;
};

const getVerificationLabel = (ticket, key) => {
  const latest = getLatestVerification(ticket, key);
  if (!latest) return "Not Started";
  return latest.decision === 1 ? "Approved" : "Rejected";
};

const getVerificationBadgeClass = (ticket, key) => {
  const latest = getLatestVerification(ticket, key);
  if (!latest) return "bg-gray-100 text-gray-500";
  return latest.decision === 1
    ? "bg-green-100 text-green-700"
    : "bg-red-100 text-red-700";
};

const successfulCallCount = computed(() => {
  return (
    selectedTicket.value?.callAttempts?.filter(
      (a) => a.outcome === "reached" || a.outcome === "confirmed"
    ).length || 0
  );
});

// Filtered tickets
const filteredTickets = computed(() => {
  return myTickets.value.filter((ticket) => {
    const search = filters.value.search.toLowerCase();

    const matchesSearch =
      !filters.value.search ||
      ticket.ticket_number.toLowerCase().includes(search) ||
      ticket.client?.full_name?.toLowerCase().includes(search) ||
      ticket.client?.phone?.toLowerCase().includes(search);

    const matchesStatus =
      !filters.value.status || ticket.decision === filters.value.status;

    return matchesSearch && matchesStatus;
  });
});

const totalPages = computed(() =>
  Math.ceil(filteredTickets.value.length / itemsPerPage.value)
);
const paginationStart = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);
const paginationEnd = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, filteredTickets.value.length)
);

// Helper functions
const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getLastCallAttempt = (ticket) => {
  if (!ticket.callAttempts?.length) return "No calls yet";
  const last = ticket.callAttempts[ticket.callAttempts.length - 1];
  return formatDateTime(last.timestamp);
};

// Event handlers
const handleOutcomeChange = () => {
  showNextSteps.value = ["no_answer", "busy", "call_back_later"].includes(
    callForm.value.outcome
  );
  if (!showNextSteps.value) {
    callForm.value.nextStep = "";
    callForm.value.callbackTime = "";
  }
};

// Actions
const resetFilters = () => {
  filters.value = {
    search: "",
    status: "",
    priority: "",
    date: "",
  };
  currentPage.value = 1;
};

const viewTicketDetails = (ticket) => {
  router.push(`/admin/tickets/${ticket.id}`);
};

const viewTicketSMS = (ticket) => {
  router.push(`/admin/sms/${ticket}`);
};

const closeCallModal = () => {
  showCallModal.value = false;
  selectedTicket.value = null;
};

const submitCallAttempt = () => {
  submitting.value = true;

  // Find contact name
  let contactName = "";
  if (callForm.value.contactType === "customer") {
    contactName = selectedTicket.value.customer.name;
  } else {
    const contact = selectedTicket.value.emergencyContacts.find(
      (c) => c.id === parseInt(callForm.value.contactId)
    );
    contactName = contact?.name || "";
    if (contact) {
      contact.status =
        callForm.value.outcome === "reached" ||
        callForm.value.outcome === "confirmed"
          ? "reached"
          : "failed";
    }
  }

  // Create new call attempt
  const newAttempt = {
    id: Date.now(),
    attemptNumber: (selectedTicket.value.callAttempts?.length || 0) + 1,
    contactName: contactName,
    contactType: callForm.value.contactType,
    phoneNumber: callForm.value.phoneNumber,
    outcome: callForm.value.outcome,
    notes: callForm.value.notes,
    timestamp: new Date().toISOString(),
    nextStep: callForm.value.nextStep || null,
    callbackTime: callForm.value.callbackTime || null,
  };

  if (!selectedTicket.value.callAttempts) {
    selectedTicket.value.callAttempts = [];
  }
  selectedTicket.value.callAttempts.push(newAttempt);

  // Update ticket status based on outcome
  if (callForm.value.outcome === "confirmed") {
    selectedTicket.value.status = "completed";
  } else if (selectedTicket.value.status === "Pending") {
    selectedTicket.value.status = "In Progress";
  }

  submitting.value = false;
  closeCallModal();
  showNotificationMessage("Call attempt logged successfully");
};

// Complete modal
const openCompleteModal = (ticket) => {
  selectedTicket.value = ticket;
  completionForm.value = {
    decision: "",
    summary: "",
  };
  showCompleteModal.value = true;
};

const closeCompleteModal = () => {
  showCompleteModal.value = false;
  selectedTicket.value = null;
};

const submitCompletion = () => {
  submitting.value = true;

  selectedTicket.value.status = "completed";
  selectedTicket.value.completedAt = new Date().toISOString();
  selectedTicket.value.completion = {
    decision: completionForm.value.decision,
    summary: completionForm.value.summary,
    completedBy: currentUser.value,
    completedAt: new Date().toISOString(),
  };

  submitting.value = false;
  closeCompleteModal();
  showNotificationMessage("Ticket completed successfully");
};

// History modal
const closeHistoryModal = () => {
  showHistoryModal.value = false;
  selectedTicket.value = null;
};

// Notification
const showNotificationMessage = (message) => {
  notificationMessage.value = message;
  showNotification.value = true;
  setTimeout(() => {
    showNotification.value = false;
  }, 3000);
};

const getTickets = async () => {
  isRefreshing.value = true;
  try {
    const dateParam = filters.value.date ? `/${filters.value.date}` : '';
    const response = await api.get(`/api/v2/tickets/all${dateParam}`);
    tickets.value = response.data.data;
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load tickets", details, 5000);
  } finally {
    isRefreshing.value = false;
  }
};

onMounted(() => {
  getTickets();
});
</script>

<style scoped>
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}
</style>